import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getCasinoWallet, blackjackStart, blackjackHit, blackjackStand, blackjackDouble } from "../lib/api";

const SUIT_SYMBOLS = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const SUIT_COLORS = { spades: "text-black", hearts: "text-red-600", diamonds: "text-red-600", clubs: "text-black" };

function Card({ card, small }) {
  if (card.hidden) {
    return (
      <div className={`border border-neutral-300 rounded-lg bg-neutral-200 flex items-center justify-center ${small ? "w-12 h-16" : "w-16 h-22"}`}>
        <span className="text-xl text-neutral-400">?</span>
      </div>
    );
  }
  const suitSym = SUIT_SYMBOLS[card.suit] || card.suit;
  const colorClass = SUIT_COLORS[card.suit] || "text-black";
  return (
    <div className={`border border-neutral-200 rounded-lg bg-white shadow-sm flex flex-col items-center justify-center ${small ? "w-12 h-16" : "w-16 h-22"} ${colorClass}`}
         style={small ? {} : {height: 88}}>
      <span className={`font-mono font-bold ${small ? "text-sm" : "text-lg"}`}>{card.rank}</span>
      <span className={small ? "text-xs" : "text-sm"}>{suitSym}</span>
    </div>
  );
}

const BET_OPTIONS = [1, 5, 10, 25, 50, 100];

export default function Blackjack() {
  const [wallet, setWallet] = useState(null);
  const [bet, setBet] = useState(10);
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getCasinoWallet().then(setWallet).catch(() => {});
  }, []);

  const updateFromResponse = (res) => {
    setGame(res);
    setWallet(w => ({ ...w, coins: res.coins, tokens: res.tokens }));
  };

  const onStart = useCallback(async () => {
    setLoading(true);
    try {
      const res = await blackjackStart(bet);
      updateFromResponse(res);
    } catch (e) {
      setGame({ error: e.message });
    } finally { setLoading(false); }
  }, [bet]);

  const onHit = async () => {
    setLoading(true);
    try { updateFromResponse(await blackjackHit()); }
    catch (e) { setGame(g => ({ ...g, error: e.message })); }
    finally { setLoading(false); }
  };

  const onStand = async () => {
    setLoading(true);
    try { updateFromResponse(await blackjackStand()); }
    catch (e) { setGame(g => ({ ...g, error: e.message })); }
    finally { setLoading(false); }
  };

  const onDouble = async () => {
    setLoading(true);
    try { updateFromResponse(await blackjackDouble()); }
    catch (e) { setGame(g => ({ ...g, error: e.message })); }
    finally { setLoading(false); }
  };

  const onNewGame = () => {
    setGame(null);
  };

  const resultText = game?.result;
  const resultColor = {
    blackjack: "text-yellow-500",
    win: "text-green-600",
    push: "text-neutral-500",
    bust: "text-red-500",
    lose: "text-red-500",
  };

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <button className="text-sm text-neutral-500" onClick={() => navigate("/casino")}>← casino</button>
        <h1 className="text-2xl font-mono">blackjack</h1>
        <div className="text-right">
          <div className="text-[10px] text-neutral-400">tokens</div>
          <div className="text-sm font-mono">{wallet?.tokens ?? 0}</div>
        </div>
      </div>

      {wallet && (
        <div className="px-6 pb-2 text-center">
          <span className="font-mono text-lg">{wallet.coins} coins</span>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {!game || game.error ? (
          /* Betting screen */
          <div className="w-full max-w-sm flex flex-col items-center gap-6">
            {game?.error && (
              <div className="text-sm text-red-500 text-center">{game.error}</div>
            )}
            <div className="w-full">
              <div className="text-[11px] text-neutral-500 text-center mb-2">bet amount</div>
              <div className="flex gap-2 justify-center flex-wrap">
                {BET_OPTIONS.map(b => (
                  <button
                    key={b}
                    className={`rounded-full px-4 py-2 text-sm font-mono border transition-colors ${
                      bet === b ? "bg-black text-white border-black" : "bg-white text-black border-neutral-200"
                    }`}
                    onClick={() => setBet(b)}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
            <button
              className={`rounded-full px-10 py-4 text-lg font-medium transition-colors ${
                !wallet || wallet.tokens < 1 || wallet.coins < bet
                  ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                  : "bg-black text-white"
              }`}
              disabled={loading || !wallet || wallet.tokens < 1 || wallet.coins < bet}
              onClick={onStart}
            >
              {loading ? "Dealing..." : "Deal (1 token)"}
            </button>
            {wallet && wallet.tokens < 1 && (
              <div className="text-xs text-neutral-400">No tokens — do an activity to earn one!</div>
            )}
          </div>
        ) : (
          /* Game in progress */
          <div className="w-full max-w-sm flex flex-col items-center gap-5">
            {/* Dealer */}
            <div className="w-full">
              <div className="text-[11px] text-neutral-500 mb-1.5">
                Dealer {game.gameOver ? `· ${game.dealerValue}` : ""}
              </div>
              <div className="flex gap-2 justify-center">
                {game.dealerHand?.map((c, i) => <Card key={i} card={c} />)}
              </div>
            </div>

            {/* Result */}
            {game.gameOver && resultText && (
              <div className={`text-center ${resultColor[resultText] || "text-neutral-600"}`}>
                <div className="text-xl font-mono font-bold uppercase">{resultText}</div>
                {resultText === "push" ? (
                  <div className="text-sm">bet returned</div>
                ) : game.payout > 0 ? (
                  <div className="text-sm">+{game.payout} coins</div>
                ) : (
                  <div className="text-sm">-{game.bet} coins</div>
                )}
              </div>
            )}

            {/* Player */}
            <div className="w-full">
              <div className="text-[11px] text-neutral-500 mb-1.5">
                You · {game.playerValue}
              </div>
              <div className="flex gap-2 justify-center">
                {game.playerHand?.map((c, i) => <Card key={i} card={c} />)}
              </div>
            </div>

            <div className="text-xs text-neutral-400 font-mono">bet: {game.bet} coins</div>

            {/* Actions */}
            {!game.gameOver ? (
              <div className="flex gap-3">
                <button
                  className="rounded-full px-6 py-3 text-sm bg-black text-white"
                  onClick={onHit} disabled={loading}
                >Hit</button>
                <button
                  className="rounded-full px-6 py-3 text-sm border border-neutral-200"
                  onClick={onStand} disabled={loading}
                >Stand</button>
                {game.canDouble && (
                  <button
                    className={`rounded-full px-6 py-3 text-sm border transition-colors ${
                      wallet && wallet.coins >= game.bet
                        ? "border-yellow-400 text-yellow-700 bg-yellow-50"
                        : "border-neutral-200 text-neutral-300 cursor-not-allowed"
                    }`}
                    onClick={onDouble}
                    disabled={loading || !wallet || wallet.coins < game.bet}
                  >Double</button>
                )}
              </div>
            ) : (
              <button
                className="rounded-full px-8 py-3 text-sm bg-black text-white"
                onClick={onNewGame}
              >New Game</button>
            )}
          </div>
        )}
      </div>

      <div className="h-6" />
    </div>
  );
}
